<template>
  <div>
    <div class="d-flex align-items-center gap-3 mb-3">
      <h1 class="mb-0 me-3">Users</h1>
      <div class="btn-group btn-group-sm ms-auto" role="group">
        <button type="button" class="btn" :class="filter === 'onboarded' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setFilter('onboarded')">Onboarded</button>
        <button type="button" class="btn" :class="filter === 'orphaned' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setFilter('orphaned')">Orphaned</button>
        <button type="button" class="btn" :class="filter === 'all' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setFilter('all')">All</button>
      </div>
    </div>
    <transition mode="out-in">
      <div v-if="loading"><loading-spinner /></div>
      <div v-else class="overflow-auto">

        <paginator-bar :page="page" :per-page="limit" :total-items="totalItems" @next-page="nextPage" />

        <table class="table table-striped table-condensed">
          <thead>
          <tr>
            <th class="pointer" @click="setSort('user')">User <span class="sort-caret">{{ sortCaret('user') }}</span></th>
            <th class="pointer" @click="setSort('user_join_date')">Joined <span class="sort-caret">{{ sortCaret('user_join_date') }}</span></th>
            <th>eShop</th>
            <th>Role</th>
            <th class="pointer" @click="setSort('eshop_join_date')">Onboarded <span class="sort-caret">{{ sortCaret('eshop_join_date') }}</span></th>
            <th class="pointer" @click="setSort('last_login_date')">Latest <span class="sort-caret">{{ sortCaret('last_login_date') }}</span></th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="user in users" :key="user.id" class="admin-user-row">
            <td class="small">
              <i class="bi bi-circle-fill small me-2" :class="{'text-success':parseInt(user.has_password)===1, 'text-warning':parseInt(user.has_password)===0}"></i>
              <a href="#" @click.stop.prevent="() => fetchEmailHistory(user.email)">{{ user.email }}</a>
            </td>
            <td class="small">{{ parseDate(user.user_join_date) }}</td>
            <td class="small"><router-link v-if="user.eshop_id" :to="{name:'AdminEshopDetails',params:{eshopId: user.eshop_id || ''}}">{{ user.eshop_id || '' }}</router-link></td>
            <td class="small"><span class="badge" :class="{'bg-secondary':user.role === 'BASIC', 'bg-success':user.role==='OWNER'}">{{ user.role }}</span></td>
            <td class="small">{{ parseDate(user.eshop_join_date) }}</td>
            <td class="small">{{ parseDate(user.last_login_date) }}</td>
            <td class="text-right">
              <button type="button" class="btn btn-sm btn-outline-secondary btn-assume" @click.stop.prevent="assumeUser(user)">Assume</button>
            </td>
          </tr>
          </tbody>
        </table>

        <paginator-bar :page="page" :per-page="limit" :total-items="totalItems" @next-page="nextPage" />
      </div>
    </transition>
    <app-modal :modal-in="selectedUser" @close="selectedUser=null;emailHistory=null" modal-class="modal-lg">
      <template #header>{{  selectedUser }} Email History</template>
      <div>
        <loading-spinner v-if="historyLoading" />
        <div v-else-if="emailHistory">
          <div class="row mb-3">
            <div class="col-4">
              <div class="card"><div class="card-body">
                <strong>DELIVERED</strong>
                <div class="display-5">{{ emailHistory.DeliveredCount }}</div>
              </div></div>
            </div>
            <div class="col-4">
              <div class="card"><div class="card-body">
                <strong>OPENED</strong>
                <div class="display-5">{{ emailHistory.OpenedCount }}</div>
              </div></div>
            </div>
            <div class="col-4">
              <div class="card"><div class="card-body">
                <strong>CLICKED</strong>
                <div class="display-5">{{ emailHistory.ClickedCount }}</div>
              </div></div>
            </div>
          </div>

          <div class="list-group">
            <div v-for="message in emailHistory.messages" :key="message.id" class="list-group-item">
              <div class="d-flex">
                <div class="col-2 small">{{ new Date(message.ArrivedAt).toLocaleDateString('en-US', {}) }}</div>
                <div class="col-8 small">Subject: <strong>{{ message.Subject }}</strong></div>
                <div class="col-2 text-end"><span class="badge bg-dark">{{ message.Status }}</span></div>
              </div>
            </div>
          </div>

        </div>

        <div v-else>No Email History Found.</div>
      </div>
    </app-modal>
  </div>
</template>

<script setup>

import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
import PaginatorBar from "@/app/components/Utils/PaginatorBar.vue";

const loading = ref(false)
const users = ref([])
const filter = ref('onboarded')
const selectedUser = ref(null)
const emailHistory = ref(null)
const historyLoading = ref(false)
const store = useStore()

const page = ref(1)
const limit = ref(30)
const totalItems = ref(0)
const sortBy = ref('user_join_date')
const sortOrder = ref('desc')

onMounted(async () => {
  loading.value = true
  await fetchUsers()
  loading.value = false
})

function parseDate(date) {
  if (!date) return '-'
  return new Date(date).toLocaleDateString()
}

async function fetchUsers() {
  const response = await store.state.api.get('/admin/users/', {
    params: {
      filter: filter.value,
      page: page.value,
      limit: limit.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value
    }
  })
  users.value = response.data?.users || [];
  totalItems.value = response.data?.total || 0
}

async function nextPage(dir) {
  page.value += dir
  await fetchUsers()
}

function setSort(column) {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'asc'
  }
  page.value = 1
  fetchUsers()
}

function sortCaret(column) {
  if (sortBy.value !== column) return ''
  return sortOrder.value === 'asc' ? '▲' : '▼'
}

async function fetchEmailHistory(email) {
  historyLoading.value = true
  emailHistory.value = null
  selectedUser.value = email
  const response = await store.state.api.get('/admin/users/email-history', { params: { email }})
  emailHistory.value = response.data?.history
  historyLoading.value = false
}

async function setFilter(value) {
  filter.value = value
  page.value = 1
  loading.value = true
  await fetchUsers()
  loading.value = false
}

const isGenerating = ref(false);

const assumeUser = async (user) => {
  try {
    isGenerating.value = true;

    // Generate magic link via your API
    const response = await fetch('/api/admin/users/get-magic-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email, returnPath: `${document.location.protocol}//${document.location.hostname}:${document.location.port}` }),
    });

    const { magicLink, error } = await response.json();
    if (error || !magicLink) {
      alert(error || 'Magic link was not created.');
      return;
    }

    // Copy to clipboard — Safari is strict about the Clipboard API so handle failure gracefully
    const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    const shortcut = isMac ? '⌘+Shift+N' : 'Ctrl+Shift+N';
    let copied = false;
    try {
      await navigator.clipboard.writeText(magicLink);
      copied = true;
    } catch {
      // Clipboard not available (common on Safari); fall through to prompt
    }

    if (copied) {
      alert(
          `Magic link copied to clipboard!\n\n` +
          `Instructions:\n` +
          `1. Press ${shortcut} to open an incognito window\n` +
          `2. Paste the link (${isMac ? '⌘+V' : 'Ctrl+V'}) into the address bar\n` +
          `3. Press Enter to assume user session`
      );
    } else {
      // Clipboard unavailable — show link so user can copy manually
      prompt('Clipboard unavailable. Copy this magic link manually:', magicLink);
    }

    // Optional: Show a toast instead of alert
    // toast.success('Magic link copied! Open incognito window and paste.', { duration: 5000 });

  } catch (error) {
    console.error('Failed to generate magic link:', error);
    alert('Failed to generate magic link. Please try again.');
  } finally {
    isGenerating.value = false;
  }
};
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
.sort-caret {
  font-size: 0.65em;
}
.admin-user-row {
  .btn-assume {
    opacity:0;
    transition: opacity 0.3s;
  }
  &:hover {
    .btn-assume {
      opacity: 1;
    }
  }
  @media (hover: none) {
    .btn-assume {
      opacity:1 !important;
    }
  }
}
</style>